import { put, takeEvery, take } from 'redux-saga/effects'
import { eventChannel, END } from 'redux-saga'
import * as SentryInstance from '@sentry/react'
import moment from 'moment'

import API from '../api-client'
import {
  getExplainSetup,
  getAllGaMetrics,
  saveExplainSetup,
  addMetric,
  updateMetric,
  removeMetric,
  getCurrentKeyMetrics,
  getAnomalyDateDescription,
  getAllGaDimensions,
  updateAnomalyDescription,
} from '../api-client/explain-client'
import {
  getValidateResults,
  updateGeneratorMasterSettings,
  getUpdateRequests,
  setUpdateRequests,
  sendUpdateRequests,
} from '../api-client/track-client'
import {
  getPolicy,
  loginGoogleSSO,
  loginMicrosoftSSO,
} from '../api-client/auth-client'
import { createBillingUser, createClient } from '../api-client/setup-client'
import { getRemoteDashboardList } from '../api-client/report-client'
import { updateEnterpriseBillingState } from '../api-client/billing-client'
import {
  handleGoogleResponse,
  handleAdobeResponse,
  addEnterpriseAccount,
  updateAccountDescription,
  addNewUserToAccount,
  revokeEmailInvite,
  updateUserPermissions,
} from '../api-client/settings-client'
import { apiDateStringToDate } from '../helpers/dates'
import {
  setPaddleData,
  setLocalItem,
  getUserData,
  // This data object is often changed between releases
  removeLocalItem,
} from '../local-client/local-client'
import { getToken, setToken, getItemByKeyValue, sortData } from '../helpers'
import { logAction } from '../api-client/analytics-client'
import { messages } from '../core/variables'
import {
  getUserAccountDetails,
  getUserDetailsGQL,
  addUserToAccount,
  getCampaignLinkDashboardMetricData,
  getCampaignLinkDashboardMetricClickData,
  getCampaignLinkDashboardGraph,
  getCampaignLinkDashboardGraphClickData,
  getCampaignLinkDashboardTable,
  getCampaignLinkDashboardMeta,
  getCampaignLinkDashboardMetaDefaults,
  setDashboardSettings,
  getStoredCodesStats,
  getOnboardingSectionsProgress,
  getDataSourceMetricList,
  getShortLinkCandidates,
  addCodes,
  getCodesByUser,
  getCodesByAccount,
  updateCode,
  deleteCode,
  getMinCodesByAccount,
  updatePreferredHomepage,
  getSettings,
  updateQrSettings,
  updateMarketingPermission,
  quickUrlValidation,
  validateShortLinkCandidate,
  intensiveUrlValidation,
  getUserPreferredHomepageGQL,
  getEnterpriseBillingDetailGQL,
  listSavedLinkPerformanceReportsGQL,
  saveLinkPerformanceReport,
  updateLinkPerformanceUserPrefs,
  removeSavedLinkPerformanceReport,
  updateSavedLinkPerformanceReport,
} from '../api-client/graphql-client'

export const clientInstance = API

function* getSettingsGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['currentAccount'] },
    })
    const payload: any = yield getSettings()
    if (payload) {
      const { currentAccount } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { currentAccount },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['currentAccount'] },
    })
  }
}

function* validateShortLinkCandidateGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const now = new Date(Date.now())

  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['shortLinkCandidates'] },
    })
    const savedData = getUserData('upl.inc')

    if (savedData && Array.isArray(savedData)) {
      const found = getItemByKeyValue(
        savedData,
        'shortLinkID',
        action.payload.testCandidate,
      )
      if (found && found.isAvailable) {
        const { availableUntil } = found
        const diff = moment(availableUntil).utc().diff(moment(now).utc())
        const isExpired = diff < 0
        if (!isExpired) {
          yield put({
            type: 'VALIDATE_SHORT_LINK_GRAPHQL_DATA',
            payload: {
              validateShortLinkCandidate: [found],
            },
          })
          yield put({
            type: 'CLEAR_ERROR_GRAPHQL_DATA',
            payload: { error: ['shortLinkCandidates'] },
          })
          return
        }
      }
    }

    const payload: any = yield validateShortLinkCandidate(
      action.payload.testCandidate,
    )
    if (payload) {
      yield put({
        type: 'VALIDATE_SHORT_LINK_GRAPHQL_DATA',
        payload: {
          validateShortLinkCandidate: [
            payload.data.track.validateShortLinkCandidate,
          ],
        },
      })
      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['shortLinkCandidates'] },
      })
    }
  } catch {
    // eslint-disable-next-line
    // debugger

    yield put({
      type: 'INVALIDATE_SHORT_LINK_GRAPHQL_DATA',
      payload: [action.payload.testCandidate],
    })
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['shortLinkCandidates'] },
    })
  }
}

function* getShortLinkCandidatesGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const now = new Date(Date.now())
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['shortLinkCandidates'] },
    })
    const payload: any = yield getShortLinkCandidates(action.payload)
    if (payload) {
      const { shortLinkCandidates } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { shortLinkCandidates },
      })

      // Add it to the validated queue
      yield put({
        type: 'VALIDATE_SHORT_LINK_GRAPHQL_DATA',
        payload: {
          validateShortLinkCandidate: shortLinkCandidates.availableLinkIDs.map(
            (shortLinkID) => {
              return {
                availableUntil: `${moment(now)
                  .add(1, 'day')
                  .format('YYYY-MM-DD')} 00:00:00`,
                isAvailable: true,
                shortLinkID,
              }
            },
          ),
        },
      })

      yield put({
        type: 'ADD_SHORT_LINK_ID',
        payload: [...shortLinkCandidates.availableLinkIDs],
      })
    }
  } catch (e) {
    console.error(e)
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['shortLinkCandidates'] },
    })
  }
}

function* removeShortLinkCandidatesGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'REMOVE_SHORT_LINK_ID',
    payload: [...action.payload.links],
  })
  yield put({
    type: 'REMOVE_SHORT_LINK_GRAPHQL_DATA',
    payload: [...action.payload.links],
  })
}

function* getDataSourceMetricListGQL(): IterableIterator<ReduxAction | any> {
  // eslint-disable-next-line
  // debugger

  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['dataSourceMetricList'] },
    })
    const payload: any = yield getDataSourceMetricList()
    if (payload) {
      const {
        dataSourceMetricList: { dataSourceMetricList },
      } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { dataSourceMetricList },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['dataSourceMetricList'] },
    })
  }
}

function* getUserAccountDetailsGQL(): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['currentAccount', 'currentUser'] },
    })
    const payload: any = yield getUserAccountDetails()
    if (payload) {
      const { currentAccount, currentUser } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { currentAccount, currentUser },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['currentAccount', 'currentUser'] },
    })
  }
}

function* getCampaignLinkDashboardMetricDataGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardMetricData'] },
    })
    const payload: any = yield getCampaignLinkDashboardMetricData(
      action.payload,
    )
    if (payload) {
      const { campaignLinkDashboardMetricData } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { campaignLinkDashboardMetricData },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardMetricData'] },
    })
  }
}

function* getCampaignLinkDashboardMetricClickDataGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardMetricDataClickData'] },
    })
    const payload: any = yield getCampaignLinkDashboardMetricClickData(
      action.payload,
    )
    if (payload) {
      const { campaignLinkDashboardMetricDataClickData } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { campaignLinkDashboardMetricDataClickData },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardMetricDataClickData'] },
    })
  }
}

function* getCampaignLinkDashboardGraphGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardGraph'] },
    })
    const payload: any = yield getCampaignLinkDashboardGraph(action.payload)
    if (payload) {
      // console.log('action.payload', action.payload)
      // const { campaignLinkDashboardGraph } = payload.data
      // import {getKeyFromMetricValue} from '../helpers'
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { campaignLinkDashboardGraph: { ...payload.data } },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardGraph'] },
    })
  }
}

function* getCampaignLinkDashboardGraphClickDataGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardGraphClickData'] },
    })
    const payload: any = yield getCampaignLinkDashboardGraphClickData(
      action.payload,
    )
    if (payload) {
      const { campaignLinkDashboardGraphClickData } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { campaignLinkDashboardGraphClickData },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardGraphClickData'] },
    })
  }
}

function* getCampaignLinkDashboardTableGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: {
        loading: [
          'campaignLinkDashboardTable',
          'campaignLinkDashboardTableRows',
        ],
      },
    })
    const payload: any = yield getCampaignLinkDashboardTable(action.payload)
    if (payload) {
      const { campaignLinkDashboardTable } = payload.data
      // HACK: removing shortLinks percentage calculations from the response
      // https://trello.com/c/NolxCkPc/621-%F0%9F%A5%87-short-link-hide-and-debug-the-short-link-returning-click-throughs-metric
      const useData = {
        ...campaignLinkDashboardTable,
        tableData: campaignLinkDashboardTable.tableData.filter(
          (item) =>
            item.metricID !== 'shortLinkReturningClicksPercentage' &&
            item.metricID !== 'shortLinkReturningClicks' &&
            item.metricID !== 'shortLinkInitialClicks',
        ),
      }
      if (
        useData.campaignLinks &&
        useData.tableData &&
        Array.isArray(useData.campaignLinks) &&
        Array.isArray(useData.tableData)
      ) {
        const data = [...useData.campaignLinks].map((cL, i) => {
          const cols = {}
          useData.tableData.forEach((c) => {
            cols[c.metricID] = c.metricValue[i]
          })
          return {
            cL,
            ...cols,
          }
        })

        const keys = useData.tableData.map((item) => {
          const { units, metricID, helpText, metricName } = item
          return {
            show: true,
            units,
            metricID,
            helpText,
            metricName,
          }
        })
        yield put({
          type: 'UPDATE_GRAPHQL_DATA',
          payload: {
            campaignLinkDashboardTableRows: {
              tableData: data,
              columnKeys: keys,
            },
          },
        })
      }

      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { campaignLinkDashboardTable: useData },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: {
        error: ['campaignLinkDashboardTable', 'campaignLinkDashboardTableRows'],
      },
    })
  }
}

function* getCampaignLinkDashboardMetaDefaultsGQL(): IterableIterator<
  ReduxAction | any
> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardMeta'] },
    })
    const payload: any = yield getCampaignLinkDashboardMetaDefaults()
    const { campaignLinkDashboardMeta, campaignCodeGenerator } = payload.data

    // HACK: removing shortLinks percentage calculations from the response
    // https://trello.com/c/NolxCkPc/621-%F0%9F%A5%87-short-link-hide-and-debug-the-short-link-returning-click-throughs-metric
    const useData = {
      ...campaignLinkDashboardMeta,
      availableClickMetrics: campaignLinkDashboardMeta.availableClickMetrics
        ? campaignLinkDashboardMeta.availableClickMetrics.filter(
            (item) =>
              item.metricID !== 'shortLinkReturningClicksPercentage' &&
              item.metricID !== 'shortLinkReturningClicks' &&
              item.metricID !== 'shortLinkInitialClicks',
          )
        : [],
    }
    yield put({
      type: 'UPDATE_GRAPHQL_DATA',
      payload: { campaignLinkDashboardMeta: useData, campaignCodeGenerator },
    })
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardMeta'] },
    })
  }
}

function* getCampaignLinkDashboardMetaGQL(): IterableIterator<
  ReduxAction | any
> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardMeta'] },
    })
    const payload: any = yield getCampaignLinkDashboardMeta()
    const { campaignLinkDashboardMeta } = payload.data
    // HACK: removing shortLinks percentage calculations from the response
    // https://trello.com/c/NolxCkPc/621-%F0%9F%A5%87-short-link-hide-and-debug-the-short-link-returning-click-throughs-metric
    const useData = {
      ...campaignLinkDashboardMeta,
      availableClickMetrics: campaignLinkDashboardMeta.availableClickMetrics
        ? campaignLinkDashboardMeta.availableClickMetrics.filter(
            (item) =>
              item.metricID !== 'shortLinkReturningClicksPercentage' &&
              item.metricID !== 'shortLinkReturningClicks' &&
              item.metricID !== 'shortLinkInitialClicks',
          )
        : [],
    }

    yield put({
      type: 'UPDATE_GRAPHQL_DATA',
      payload: { campaignLinkDashboardMeta: useData },
    })
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardMeta'] },
    })
  }
}

function* getStoredCodeStatsGQL(): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['storedCodeStats'] },
    })
    const payload: any = yield getStoredCodesStats()
    const { storedCodeStats } = payload.data
    const { codeIDs, metricValues, status } = storedCodeStats

    const useData = sortData(
      metricValues.filter(
        (item) =>
          item.metricID !== 'shortLinkReturningClicksPercentage' &&
          item.metricID !== 'shortLinkReturningClicks' &&
          item.metricID !== 'shortLinkInitialClicks',
      ),
      'displayName',
      false,
    )

    yield put({
      type: 'UPDATE_GRAPHQL_DATA',
      payload: {
        storedCodeStats: {
          codeIDs,
          metricValues: useData,
          status,
        },
      },
    })
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['storedCodeStats'] },
    })
  }
}

function* setDashboardSettingsGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['campaignLinkDashboardMeta'] },
    })

    const payload: any = yield setDashboardSettings(action.payload)

    // const payload: any = yield getCampaignLinkDashboardMeta({
    //   matchType: 'full'
    // })
    const {
      setLinkPerformanceDashboardAccountSettings: campaignLinkDashboardMeta,
    } = payload.data
    yield put({
      type: 'UPDATE_GRAPHQL_DATA',
      payload: { campaignLinkDashboardMeta },
    })
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['campaignLinkDashboardMeta'] },
    })
  }
}

function* addCodesGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['addCodes'] },
    })

    const shortLinks = action.payload.cL
      .map(({ shortLinkID }) => shortLinkID)
      .filter((i) => typeof i === 'string')
    yield put({
      type: 'INVALIDATE_SHORT_LINK_GRAPHQL_DATA',
      payload: [...shortLinks],
    })

    const payload: any = yield addCodes(action.payload)
    if (payload && payload.data?.addCodes?.codeList?.length > 0) {
      yield put({
        type: 'PREPEND_USERS_CAMPAIGN_CODES',
        payload: payload.data.addCodes.codeList,
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['addCodes'] },
    })
  }
}

function* getCodesByUserGQL(): IterableIterator<ReduxAction | any> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['getCodesByUser'] },
    })
    const payload: any = yield getCodesByUser()
    yield put({
      type: 'GET_USERS_CAMPAIGN_CODES',
      payload: payload.data.codesByUser.codeList,
    })

    // let res
    // if (action && action.payload) {
    //   const { startDate } = action?.payload
    //   res = yield API.getUsersPreviousGeneratedCodes({ startDate })
    // } else {
    //   res = yield API.getUsersPreviousGeneratedCodes({})
    // }

    // if (!res) return
    // yield put({
    //   type: 'GET_USERS_CAMPAIGN_CODES',
    //   payload: res,
    // })
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['getCodesByUser'] },
    })
  }
}

function* addUserToAccountGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'SET_USER_LIST_LOADING_STATUS',
    payload: {
      loading: true,
    },
  })

  const { users, accounts, permission } = action.payload
  const payload: any = yield addUserToAccount({ users, accounts, permission })

  // ! Array is not meaningfully used
  // const errors: string[] = []

  const successes: string[] = []
  Object.keys(payload.data).forEach((key) => {
    const user = payload.data[key]
    const { email } = user
    if (successes.indexOf(email) === -1) {
      successes.push(email)
    }
  })

  // ! Unnecessary block
  // const error =
  //   errors.length > 0
  //     ? `Failed adding new user${
  //         errors.length > 1 ? 's' : ''
  //       } for ${errors.join(', ')}.`
  //     : ''
  //! ^ Legacy - never true
  const error = ''

  let success = ''

  // @ts-ignore
  if (successes.length === 1) {
    // All values in data are the same
    // Same user has been added to one or more accounts

    // @ts-ignore
    success = Object.values(payload.data)[0].inviteOpen
      ? `${successes[0]} has been sent a registration email and has 14 days to set a password.`
      : `${successes[0]} has been added to the selected account${
          accounts.length > 1 ? 's' : ''
        } as a${permission === 'admin' ? 'n admin' : ' regular'} user.`
  } else {
    success = `${successes.length} users (${
      successes.length > 3
        ? `${successes.slice(0, 3).join(', ')}, ${successes.length - 3} other${
            successes.length - 3 > 1 ? 's' : ''
          }`
        : successes.join(', ')
    }) have been given access to the selected account${
      accounts.length > 1 ? 's' : ''
    } as ${permission === 'admin' ? 'admin' : 'regular'} users.`
  }

  yield put({
    type: 'SET_SITE_USERS_MESSAGE',
    payload: {
      error,
      success,
    },
  })

  const userListPayload: any = yield API.getUserList()
  if (payload) {
    yield put({
      type: 'GET_USER_LIST',
      payload: userListPayload,
    })
  }

  yield put({
    type: 'SET_USER_LIST_LOADING_STATUS',
    payload: {
      loading: false,
    },
  })
}

function* updateGeneratorMasterSettingsAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'UPDATE_MASTER_SETTINGS',
    payload: action.payload,
  })
  yield updateGeneratorMasterSettings(action.payload)
}

function* getStoredCodeStatsAsync(): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'START_LOADING_CAMPAIGN_CODES',
  })

  const minRes: any = yield getMinCodesByAccount()

  if (!minRes) {
    yield put({
      type: 'FINISH_LOADING_CAMPAIGN_CODES',
    })
    return
  }

  yield put({
    type: 'GET_MIN_CAMPAIGN_CODES',
    payload: minRes.data?.minCodesByAccount,
  })
}

function* setCurrentDropDown(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'SET_DROPDOWN',
    payload: action?.payload,
  })
}

function* setCurrentTooltip(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'SET_TOOLTIP',
    payload: action?.payload,
  })
}

function* getCurrentKeyMetricsAsync(): IterableIterator<ReduxAction | any> {
  const payload: any = yield getCurrentKeyMetrics()

  if (payload) {
    yield put({
      type: 'GET_CURRENT_KEY_METRICS',
      payload,
    })
  }
}

function* getValidateResultsAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const payload: any = yield getValidateResults(action.payload)

  if (payload) {
    yield put({
      type: 'GET_VALIDATED_URLS',
      payload: { ...payload.validatedUrls },
    })
  }
}

function* getUpdateRequestsAsync(): IterableIterator<ReduxAction | any> {
  const payload: any = yield getUpdateRequests()
  if (payload) {
    yield put({
      type: 'GET_UPDATE_REQUEST',
      payload,
    })
  }
}

function* setUpdateRequestsAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'SET_UPDATE_PENDING_REQUEST',
    payload: action.payload,
  })
  yield setUpdateRequests(action.payload)
  // Fetch data back once updated
  const payload: any = yield getUpdateRequests()
  if (payload) {
    yield put({
      type: 'GET_UPDATE_REQUEST',
      payload,
    })
  }
}

function* sendUpdateRequestsAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const payload: any = yield sendUpdateRequests(action.payload)
  if (payload) {
    yield put({
      type: 'SEND_UPDATE_REQUEST',
      payload,
    })
  }
}

function* validateUrlListAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const urlList = action.payload.urlList.filter((u) => u !== '')
  if (urlList.length > 0) {
    const listToValidate = {}
    urlList.forEach((url) => {
      listToValidate[url] = null
    })
    yield put({
      type: 'START_RECENTLY_VALIDATED_URLS',
      payload: { ...listToValidate },
    })
    const quickPayload: any = yield quickUrlValidation(urlList)
    if (quickPayload) {
      yield put({
        type: 'SET_RECENTLY_VALIDATED_URLS_STATUS',
        payload: {
          validationResults:
            quickPayload.data.track.quickUrlValidation.validationResults,
        },
      })
    }

    const payload: any = yield intensiveUrlValidation(urlList)
    if (payload) {
      yield put({
        type: 'GET_RECENTLY_VALIDATED_URLS',
        payload: {
          validationResults:
            payload.data.track.intensiveUrlValidation.validationResults,
        },
      })
    }
  }
}

function* getRemoteDashboardListAsync(): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'START_LOADING_REPORTS',
  })

  const payload: any = yield getRemoteDashboardList()

  if (payload) {
    yield put({
      type: 'LOAD_REPORTS',
      payload,
    })
  }
}

function* getAnomalyDateDescriptionAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'START_LOADING_ANOMALY_DESCRIPTION',
  })

  const payload: any = yield getAnomalyDateDescription(action.payload)

  if (payload) {
    yield put({
      type: 'GET_ANOMALY_DESCRIPTION',
      payload,
    })
  }
}

function* updateAnomalyDescriptionReducersOnly(
  action: any,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'UPDATE_LOADED_ANOMALIES',
    payload: { ...action.payload },
  })
  yield put({
    type: 'UPDATE_ANOMALY_DESCRIPTION',
    payload: { ...action.payload },
  })
}

function* updateAnomalyDescriptionAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const {
    anomalyDateID,
    likedCauses,
    dislikedCauses,
    likedActions,
    dislikedActions,
    userSuppliedCause,
    userSuppliedAction,
    userSuppliedEffort,
    userSuppliedBenefit,
    selectedRelevantDimensions,
    anomalyNotInteresting,
  } = action.payload

  const data = {
    anomalyDateID,
    likedCauses,
    dislikedCauses,
    likedActions,
    dislikedActions,
    userSuppliedCause,
    userSuppliedAction,
    userSuppliedEffort,
    selectedRelevantDimensions,
    userSuppliedBenefit,
    anomalyNotInteresting,
  }

  yield updateAnomalyDescription(data)
  // const payload: any = yield updateAnomalyDescription(data)

  if (action.payload !== false) {
    yield put({
      type: 'UPDATE_ANOMALY_DESCRIPTION',
      payload: action.payload,
    })
    yield put({
      type: 'UPDATE_LOADED_ANOMALIES',
      payload: action.payload,
    })
  }
}

function* getAllGaDimensionsAsync(): IterableIterator<ReduxAction | any> {
  const payload: any = yield getAllGaDimensions()

  if (payload) {
    yield put({
      type: 'GET_ALL_GA_DIMENSIONS',
      payload,
    })
  }
}

function* getUserListAsync(): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'SET_USER_LIST_LOADING_STATUS',
    payload: {
      loading: true,
    },
  })

  const payload: any = yield API.getUserList()
  if (payload) {
    yield put({
      type: 'GET_USER_LIST',
      payload,
    })
  }

  yield put({
    type: 'SET_USER_LIST_LOADING_STATUS',
    payload: {
      loading: false,
    },
  })
}

function* getEnterpriseBillingStateAsync(): IterableIterator<
  ReduxAction | any
> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['enterpriseBillingDetail'] },
    })
    const payload: any = yield getEnterpriseBillingDetailGQL()
    if (payload) {
      const {
        accountSettings: { enterpriseBillingDetail },
      } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { enterpriseBillingDetail },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['enterpriseBillingDetail'] },
    })
  }
}

function* listSavedLinkPerformanceReportsAsync(): IterableIterator<
  ReduxAction | any
> {
  try {
    yield put({
      type: 'START_LOADING_GRAPHQL_DATA',
      payload: { loading: ['listSavedLinkPerformanceReports'] },
    })
    const payload: any = yield listSavedLinkPerformanceReportsGQL()
    if (payload) {
      const {
        report: { listSavedLinkPerformanceReports },
      } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { listSavedLinkPerformanceReports },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['listSavedLinkPerformanceReports'] },
    })
  }
}

function* updateEnterpriseBillingStateAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'START_LOADING_ENTERPRISE_DETAILS',
  })

  const res: any = yield updateEnterpriseBillingState(action.payload)

  if (res && action.payload) {
    yield put({
      type: 'LOAD_ENTERPRISE_DETAILS',
      payload: action.payload,
    })
  } else {
    yield put({
      type: 'LOAD_ENTERPRISE_DETAILS_ERROR',
    })
  }
}

function* addEnterpriseAccountAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const res: any = yield addEnterpriseAccount(action.payload)

  if (res && action.payload) {
    // yield put({
    //   type: 'LOAD_ENTERPRISE_DETAILS',
    //   payload: action.payload,
    // })
  }
}

function* updateAccountDescriptionAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const res: any = yield updateAccountDescription(action.payload)

  if (res) {
    yield put({
      type: 'UPDATE_SELECTED_SITE',
      payload: action.payload,
    })

    try {
      yield put({
        type: 'START_LOADING_GRAPHQL_DATA',
        payload: { loading: ['currentUser'] },
      })
      const payload: any = yield getUserDetailsGQL()
      if (payload) {
        const { currentUser } = payload.data
        yield put({
          type: 'UPDATE_GRAPHQL_DATA',
          payload: { currentUser },
        })
      }
    } catch {
      yield put({
        type: 'ERROR_GRAPHQL_DATA',
        payload: { error: ['currentUser'] },
      })
    }
  }
}

function* getExplainSetupAsync(): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'START_LOADING_EXPLAIN_SETUP',
  })

  const payload: any = yield getExplainSetup()

  if (payload) {
    yield put({
      type: 'GET_EXPLAIN_SETUP',
      payload,
    })
  }
}

function* removeMetricAsync(action: any): IterableIterator<ReduxAction | any> {
  if (action.payload) {
    yield put({
      type: 'REMOVE_METRIC',
      payload: action.payload,
    })
  }

  yield removeMetric({
    ...action.payload,
  })

  yield put({
    type: 'GET_CURRENT_KEY_METRICS_ASYNC',
  })

  yield put({
    type: 'GET_EXPLAIN_SETUP_ASYNC',
  })
}

// newMetric
// apiName
// detectionMethod
// increaseSentimentPositive
// sendAlert
// dataSource

function* updateMetricAsync(action: any): IterableIterator<ReduxAction | any> {
  yield updateMetric({
    ...action.payload,
  })

  // Load up change
  yield put({
    type: 'GET_EXPLAIN_SETUP_ASYNC',
  })

  yield put({
    type: 'GET_CURRENT_KEY_METRICS_ASYNC',
  })
}

function* addMetricAsync(action: any): IterableIterator<ReduxAction | any> {
  // if (action.payload) {
  // yield put({
  //   type: 'ADD_METRIC',
  //   payload: action.payload,
  // })
  // yield put({
  //   type: 'PRESET_CURRENT_KEY_METRICS',
  //   payload: {
  //     newMetric: action.payload.newMetric,
  //     newMetricName: action.payload.newMetricName,
  //   },
  // })
  // }

  yield addMetric({
    ...action.payload,
  })

  // Load up change
  yield put({
    type: 'GET_EXPLAIN_SETUP_ASYNC',
  })

  yield put({
    type: 'GET_CURRENT_KEY_METRICS_ASYNC',
  })
}

function* saveExplainSetupAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  if (action.payload) {
    yield put({
      type: 'SAVE_EXPLAIN_SETUP',
      payload: action.payload,
    })
  }
  yield saveExplainSetup({
    pageLayout: action.payload.pageLayout,
    settings: action.payload.settings,
  })
}

// function* saveCampaignCodesAsync(
//   action: any,
// ): IterableIterator<ReduxAction | any> {
//   if (action.payload) {
//     yield put({
//       type: 'SAVE_EXPLAIN_SETUP',
//       payload: action.payload,
//     })
//   }
//   yield API.saveCampaignCodes({
//     ...action.payload.data
//   })
// }

function* getPolicyAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const payload: any = yield getPolicy(action.payload)
  // eslint-disable-next-line
  // debugger

  // if (payload && payload.success) {

  yield put({
    type: 'GET_POLICY',
    payload: {
      policy: {
        lastMethod: payload.res?.data?.lastMethod || '',
        notFound: payload.res?.error?.response?.data?.notFound || false,
        domainExists: payload.res?.error?.response?.data?.domainExists || false,
      },
    },
  })
  // } else {
  //   const errorMessage = messages.sorry

  //   yield put({
  //     type: 'ON_LOGIN_ATTEMPT_FAIL',
  //     payload: errorMessage,
  //   })
  // }
}

function* getAllGaMetricsAsync(): IterableIterator<ReduxAction | any> {
  const payload: any = yield getAllGaMetrics()

  if (payload) {
    yield put({
      type: 'GET_GA_METRICS',
      payload,
    })
  }
}

function* authenticateAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const loginRes: any = yield API.login(action.payload)
  // eslint-disable-next-line
  // debugger

  // needed to compare to a string
  if (loginRes.success) {
    const result: any = yield getUserPreferredHomepageGQL()

    if (
      result &&
      result?.data &&
      result?.data?.currentUser &&
      result?.data?.currentUser.preferredHomepage
    ) {
      yield put({
        type: 'UPDATE_PREFERRED_HOMEPAGE',
        payload: result.data,
      })
    }

    yield put({
      type: 'AUTHENTICATE',
      payload: loginRes?.res?.data,
    })
    if (loginRes?.res?.data?.token) {
      SentryInstance.setUser({ id: loginRes?.res?.data?.token })
    }

    // @ts-ignore
    if (window.dataLayer && window.dataLayer.push) {
      const token = getToken()
      // @ts-ignore
      window.dataLayer.push({ event: 'login', token })
    }

    // Try to get the version of the local  and compare it with the version file
    try {
      const response: any = yield API.getVersion()
      if (response !== false) {
        const appVersion = process.env.REACT_APP_VERSION
        const { version: fetchedVersion } = response
        if (appVersion !== fetchedVersion) {
          // This data object is often changed between releases
          removeLocalItem(['user-session-data'])
          window.location.reload()
        }
      }
    } catch {
      console.error('error fetching version')
    }

    return
  }

  let errorMessage = messages.sorry

  if (loginRes && loginRes.res) {
    const { message, status, error } = loginRes.res
    if (
      status === 403 &&
      error &&
      error.response &&
      error.response.data &&
      error.response.data.error &&
      error.response.data.error === 'BAD_BILLING'
    ) {
      errorMessage =
        'This account has been suspended due to not having a valid licence. Please email support@uplifter.ai to buy a licence and reactivate your account.'
    } else {
      errorMessage = message || errorMessage
    }
  }

  yield put({
    type: 'ON_LOGIN_ATTEMPT_FAIL',
    payload: errorMessage,
  })
}

function* registerAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const res = yield API.register(action.payload)

  // needed to compare to a string
  if (res === true) {
    yield put({
      type: 'REGISTERED_SUCCESSFULLY',
    })
    yield put({
      type: 'AUTHENTICATE',
      payload: null,
    })
    return
  }

  const payload = res || messages.sorry

  yield put({
    type: 'ON_REGISTER_ATTEMPT_FAIL',
    payload,
  })

  yield put({
    type: 'ONBOARDING_CREATE_USER_FAILED',
    payload,
  })
  yield put({
    type: 'ON_LOGIN_ATTEMPT_FAIL',
    payload,
  })
}

function* checkAuthenticationAsync(): IterableIterator<ReduxAction | any> {
  const auth = yield API.testAuth()
  if (auth) {
    yield put({
      type: 'AUTHENTICATE',
    })
  } else {
    yield put({
      type: 'UNAUTHENTICATE',
    })
  }
}

function* createNewBillingUserAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const res: any = yield createBillingUser(action.payload)

  if (res) {
    setPaddleData(res)

    yield put({
      type: 'ONBOARDING_CREATED_NEW_BILLING_USER',
      payload: res,
    })
    return
  }

  let payload = messages.sorry

  if (res) {
    payload = res
  }

  yield put({
    type: 'ONBOARDING_CREATE_USER_FAILED',
    payload,
  })
}

function* resetLoginAttemptFailAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'RESET_LOGIN_ATTEMPT_FAIL',
  })
}

function* resetOnboardingCreateUserFailedAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'RESET_ONBOARDING_CREATE_USER_FAILED',
  })
}

function* createNewClientAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const res: any = yield createClient(action.payload)

  // eslint-disable-next-line
  // debugger

  if (res) {
    yield put({
      type: 'AUTHENTICATE',
      payload: {},
    })

    if (window.location.search.indexOf('utm_source=champion_page') !== -1) {
      const actionData = {
        action: 'new-champion',
        longDescription: window.location.search,
        websiteSection: 'create-account',
      }
      yield logAction(actionData)
    }
    return
  }

  let payload = messages.sorry

  if (res) {
    const { status } = res
    if (status === 403 || status === 400) {
      payload = messages.alreadyHaveAccount
    }
  }

  yield put({
    type: 'ONBOARDING_CREATE_USER_FAILED',
    payload,
  })
  yield put({
    type: 'ON_LOGIN_ATTEMPT_FAIL',
    payload,
  })
}

function* getGooglePermissions(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.getGooglePermissions(action.payload)
}

function* handleGoogleResponseAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const dataSource = 'GA_VIEW'
  yield put({
    type: 'START_OAUTH',
    payload: dataSource as DataSource,
  })
  yield API.setDataSource(dataSource)

  const result = yield handleGoogleResponse(action.payload)

  if (result) {
    const { success, res } = result
    if (success && res) {
      const {
        data: { credentials },
      } = res
      yield API.setCredentials(credentials)
    }
  }

  yield put({
    type: 'FINISH_OAUTH',
    payload: result,
  })
}

function* loginGoogleSSOAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const result = yield loginGoogleSSO(action.payload)

  if (result) {
    // eslint-disable-next-line
    // debugger

    const {
      success,
      res: { data },
    } = result
    if (success) {
      const homepage: any = yield getUserDetailsGQL()

      if (
        homepage &&
        homepage?.data &&
        homepage?.data?.currentUser &&
        homepage?.data?.currentUser.preferredHomepage
      ) {
        yield put({
          type: 'UPDATE_PREFERRED_HOMEPAGE',
          payload: homepage.data,
        })
      }

      const { googleKey, token } = data
      if (googleKey && token) {
        setLocalItem('ga-googleKey', googleKey)
        setToken(token)
        yield put({
          type: 'AUTHENTICATE',
          payload: {},
        })
      }
    } else {
      yield put({
        type: 'ON_LOGIN_ATTEMPT_FAIL',
        payload: messages.sorry,
      })
    }
  }
}

function* loginMicrosoftSSOAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const result = yield loginMicrosoftSSO(action.payload)

  if (result) {
    const {
      success,
      res: { data },
    } = result

    if (success) {
      const homepage: any = yield getUserDetailsGQL()

      if (
        homepage &&
        homepage?.data &&
        homepage?.data?.currentUser &&
        homepage?.data?.currentUser.preferredHomepage
      ) {
        yield put({
          type: 'UPDATE_PREFERRED_HOMEPAGE',
          payload: homepage.data,
        })
      }

      const { microsoftKey, token } = data
      if (success && microsoftKey && token) {
        setLocalItem('ms-microsoftKey', microsoftKey)
        setToken(token)
        yield put({
          type: 'AUTHENTICATE',
          payload: {},
        })
      }
    } else {
      yield put({
        type: 'ON_LOGIN_ATTEMPT_FAIL',
        payload: messages.sorry,
      })
    }
  }
}

function* handleAdobeResponseAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const dataSource = 'AA_REPORT'
  yield put({
    type: 'START_OAUTH',
    payload: dataSource as DataSource,
  })
  yield API.setDataSource(dataSource)

  const result = yield handleAdobeResponse(action.payload)

  if (result) {
    const { success, res } = result
    if (success && res) {
      const {
        data: { credentials },
      } = res
      yield API.setCredentials(credentials)
    }
  }

  yield put({
    type: 'FINISH_OAUTH',
    payload: result,
  })
}

function* loadCredentialsAsync(): IterableIterator<ReduxAction | any> {
  // GA credentials may be stored in local storage, try to get them
  const credentials = yield API.getCredentials()
  const dataSource = yield API.getDataSource()
  if (credentials) {
    yield put({
      type: 'SET_CREDENTIALS_OAUTH',
      payload: { credentials, dataSource },
    })
  }
}

function* resetPasswordAsync(action: any): IterableIterator<ReduxAction | any> {
  const { token, password } = action.payload

  const res = yield API.resetPassword(token, password)

  yield put({
    type: 'RESET_PASSWORD',
    payload: res,
  })

  if (res) {
    yield put({
      type: 'AUTHENTICATE',
      payload: {},
    })
  }
}

function* requestPasswordResetAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  // yield put({
  //   type: 'REQUEST_PASSWORD_RESET',
  // })

  const { payload } = action

  const res = yield API.requestPasswordReset(payload)

  yield put({
    type: 'REQUEST_PASSWORD_RESET',
    payload: res,
  })
}

function* getAnomalyDataRange(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const { metricID, from, to, selector } = action.payload

  yield put({
    type: 'START_NEW_ANOMALY_DATE_RANGE_SEARCH',
    payload: metricID,
  })

  const res: any = yield API.getAnomalyDataRange(metricID, from, to)

  if (res && res.data && res.data.anomalyRange) {
    yield put({
      type: 'UPDATE_ANOMALY_GRAPH',
      payload: {
        metricID,
        ...res.data,
        lastRange: {
          from,
          to,
          selector,
        },
        callData: {
          loading: false,
        },
      },
    })
  } else {
    yield put({
      type: 'FAILED_LOADING_ANOMALY_GRAPH',
      payload: {
        metricID,
        lastRange: {
          from,
          to,
          selector,
        },
      },
    })
  }
}

// function* getAnomalyBreakdown(
//   action: ReduxAnomalyBreakdown,
// ): IterableIterator<ReduxAction | any> {
//   const { id } = action.payload
//   const [metricID, nonFormattedDate] = id.split('/')
//   const date = nonFormattedDate.split('-').reverse().join('')

//   // yield put({
//   //   type: 'LOADING_ANOMALY_BREAKDOWN',
//   //   payload: id,
//   // })

//   const data: any = yield API.getAnomalyByDateAndMetric(date, metricID)
//   if (!data) return

//   const payload: any = {
//     metric: metricID,
//   }
//   payload[id] = {
//     ...data,
//   }

//   yield put({
//     type: 'GET_ANOMALY_BREAKDOWN',
//     payload,
//   })
// }

function* getCausesAndSolutions(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const {
    anomalyDateID,
  }: {
    anomalyDateID: string
  } = action.payload

  yield put({
    type: 'START_LOADING_ANOMALY_BREAKDOWN_CAUSES',
  })

  const payload: any = yield API.getCausesAndSolutions(anomalyDateID)
  if (!payload) {
    yield put({
      type: 'GET_ANOMALY_BREAKDOWN_CAUSES',
      payload: { recommendedCauses: [] },
    })
    return
  }
  // const causesFormatted = causes.map((cause: any): any => ({
  //   solutions: cause.actions.map((solution: any): any => ({
  //     solution: solution.action,
  //     databaseID: solution.databaseID,
  //     author: {
  //       src:
  //         'https://uplifterblobstorage.blob.core.windows.net/profilepics/alexProfilePic.png',
  //       name: solution.userName,
  //       role: solution.publicJobTitle,
  //       date: '25/12/2019',
  //     },
  //   })),
  //   cause: cause.cause,
  //   databaseID: cause.databaseID,
  //   author: {
  //     src:
  //       'https://uplifterblobstorage.blob.core.windows.net/profilepics/alexProfilePic.png',
  //     name: cause.userName,
  //     role: cause.publicJobTitle,
  //     date: '25/12/2019',
  //   },
  // }))

  yield put({
    type: 'GET_ANOMALY_BREAKDOWN_CAUSES',
    payload,
  })
}

function* getAllUsers(): IterableIterator<ReduxAction | any> {
  const payload = yield API.getAllUsers()

  yield put({
    type: 'GET_ALL_USERS',
    payload,
  })
}

function* subscribeToAudit(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'CLEAR_AUDIT_DATA',
  })
  const socket = (emitter: any): VoidFunction =>
    API.auditSubscribe(
      (payload: any, close: VoidFunction): void => {
        emitter({ payload })
        if (payload.currentTestsRan === payload.totalTestsToRun) {
          close()
          emitter(END)
        }
      },
      action.payload.uid,
      action.payload.runningDebug,
    )

  const channel = eventChannel(socket)

  while (true) {
    const { payload }: any = yield take(channel)
    if (!payload) {
      break
    }
    yield put({
      type: 'UPDATE_AUDIT_DATA',
      payload,
    })
  }

  // NOTE: this is how to do polling in generators
  // while (true) {
  //   const payload: any = yield API.pollAuditAsync(taskID)
  //   if (payload) {
  //     yield put({
  //       type: 'UPDATE_AUDIT_PROGRESS',
  //       payload,
  //     })
  //     const { progressPercentage, state } = payload
  //     if (progressPercentage === 100 || (state && state !== 'PROGRESS')) {
  //       yield put({
  //         type: 'UPDATE_AUDIT_DATA_ASYNC',
  //         payload,
  //       })
  //       break
  //     }
  //   }
  //   yield delay(500)
  // }
}

function* requestAnExpert(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const payload = yield API.requestAnExpert(action.payload)

  // @ts-ignore
  if (window.dataLayer && window.dataLayer.push) {
    const token = getToken()
    // @ts-ignore
    window.dataLayer.push({ event: 'submit-request-an-expert', token })
  }

  yield put({
    type: 'REQUEST_AN_EXPERT',
    payload,
  })
}

function* addAccount(action: any): IterableIterator<ReduxAction | any> {
  const res = yield API.addAccount(action.payload)

  if (action.payload.initial && res) {
    yield put({
      type: 'TOGGLE_ONBOARDING_CONNECT_ACCOUNT',
    })
  }
}

function* addNewUsersToAccountAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  const { emails, accountID, permissionLevel } = action.payload
  // https://zellwk.com/blog/async-await-in-loops/
  const promises = emails.map(async (eml: string) => {
    return addNewUserToAccount({
      email: eml,
      accountID,
      permissionLevel,
    })
  })

  const res: any = yield Promise.all(promises)
  // eslint-disable-next-line
  // debugger

  const pendingUsers: PendingUserWithPermission[] = []
  const existingUsers: UserWithPermission[] = []
  const errors: string[] = []
  const successes: string[] = []
  if (typeof res !== undefined && Array.isArray(res)) {
    res.forEach((item, index) => {
      if (item) {
        const { permission: userItem } = item
        if (userItem) {
          const {
            inviteOpen,
            created,
            permission,
            lastLogin,
            email,
            userId,
          } = userItem
          if (inviteOpen) {
            pendingUsers.push({
              email,
              expired: false,
              permissionLevel: permission,
              sentTime: created,
            })
          } else {
            existingUsers.push({
              email,
              lastLogin,
              permission,
              userID: userId,
            })
          }
        }
        successes.push(emails[index])
      } else {
        errors.push(emails[index])
      }
    })
  }
  if (pendingUsers.length > 0) {
    yield put({
      type: 'ADD_PENDING_USERS',
      payload: pendingUsers,
    })
  }
  if (existingUsers.length > 0) {
    yield put({
      type: 'ADD_EXISTING_USERS',
      payload: existingUsers,
    })
  }
  const error =
    errors.length > 0
      ? `Failed adding new user${
          errors.length > 1 ? 's' : ''
        } for ${errors.join(', ')}.`
      : ''
  const success =
    successes.length > 0
      ? `User${successes.length > 1 ? 's' : ''} (${successes.join(', ')}) ${
          successes.length > 1 ? 'have' : 'has'
        } been added. If they do not have an account yet, they will be invited to join Uplifter.`
      : ''
  // eslint-disable-next-line
  // debugger

  yield put({
    type: 'SET_SITE_USERS_MESSAGE',
    payload: {
      error,
      success,
    },
  })
  // const noErrors = typeof res !== undefined && Array.isArray(res) ? res.filter((a) => !a) : []
  // console.log(errors)
}

interface UserWithPermission {
  email?: string
  lastLogin?: string
  permission: PermissionLevel
  userID: string
}

function* removeUserAsync(action: any): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'REMOVE_EXISTING_USER',
    payload: action.payload,
  })
  yield API.removeUser(action.payload)
}

function* revokeEmailInviteAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'REVOKE_EMAIL_INVITE',
    payload: action.payload,
  })
  yield revokeEmailInvite(action.payload)
}

function* updateUserPermissionsAsync(
  action: any,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'UPDATE_USER_PERMISSIONS',
    payload: action.payload,
  })
  yield updateUserPermissions(action.payload)
}

function* changeUserPermissions(
  action: any,
): IterableIterator<ReduxAction | any> {
  yield API.changeUserPermissions(action.payload)
}

function* closeAccounts(): IterableIterator<ReduxAction | any> {
  yield API.closeAccounts()
}

function* logout(): IterableIterator<ReduxAction | any> {
  yield API.logout()
  yield put({
    type: 'LOGOUT',
  })
}

function* getSelectedAccount(): IterableIterator<ReduxAction | any> {
  const selected = yield API.getAccountSelected()
  if (selected) {
    yield put({
      type: 'GET_SELECTED_ACCOUNT',
      payload: selected,
    })
  }
}

function* getUserDetails(): IterableIterator<ReduxAction | any> {
  const payload = yield API.getUserDetails()

  if (payload) {
    yield put({
      type: 'UPDATE_USER_DETAILS',
      payload,
    })
  }
}

function* switchAccount(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.switchAccount(action.payload)
  const payload = yield API.getAccountSelected()

  yield put({
    type: 'GET_SELECTED_ACCOUNT',
    payload,
  })
}

function* logActionAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  // Exclude logging on localhost
  const isOverRide =
    process.env.REACT_APP_STAGING_OVERRIDE === 'true' &&
    process.env.REACT_APP_NODE_ENV !== 'production'
  if (!isOverRide) {
    yield logAction(action.payload)
  }
}

function* getCalendarAnomalies(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const { fromDate, toDate } = action.payload
  const res: any = yield API.getAnomalyRange(fromDate, toDate)
  if (!res) return
  // const anomalies = res.anomalyRange.map((anomaly: any): any => ([
  //   apiDateStringToDate(anomaly.date).toString(),
  //   [anomaly],
  // ]));

  const anomalies = new Map()
  res.anomalyRange.forEach((anomaly: AnomalyCalendarItem): void => {
    if (anomalies.get(apiDateStringToDate(anomaly.date).toString())) {
      const nextDate = apiDateStringToDate(anomaly.date).toString()
      anomalies.set(nextDate, [...anomalies.get(nextDate), anomaly])
    } else {
      anomalies.set(apiDateStringToDate(anomaly.date).toString(), [anomaly])
    }
  })

  yield put({
    type: 'UPDATE_CAL_ANOMALY_RANGE',
    payload: anomalies,
  })
}

function* getGaCampaigns(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const { fromDate, toDate } = action.payload
  const res: any = yield API.getGaCampaigns(fromDate, toDate)
  if (!res) return
  yield put({
    type: 'UPDATE_CAL_GA_CAMPAIGNS',
    payload: res.campaignNames,
  })
}

function* getCalendarCampaigns(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const { fromDate, toDate } = action.payload
  const res: any = yield API.getCampaignRange(fromDate, toDate)
  if (!res) return
  const campaigns = res.campaignList.map((campaign: any): any => [
    apiDateStringToDate(campaign.startDate).toString(),
    campaign,
  ])
  yield put({
    type: 'UPDATE_CAL_CAMPAIGNS',
    payload: campaigns,
  })
}

function* getCalendarEvents(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const { fromDate, toDate } = action.payload
  const res: any = yield API.getEventRange(fromDate, toDate)
  if (!res) return
  const events = new Map()
  res.eventList.forEach((event: any): void => {
    if (events.get(apiDateStringToDate(event.startDate).toString())) {
      const nextDate = apiDateStringToDate(event.startDate).toString()
      events.set(nextDate, [...events.get(nextDate), event])
    } else {
      events.set(apiDateStringToDate(event.startDate).toString(), [event])
    }
  })

  yield put({
    type: 'UPDATE_CAL_EVENTS',
    payload: events,
  })
}

function* getKpiMetrics(): IterableIterator<ReduxAction | any> {
  const res: any = yield API.getKpiMetrics()
  if (!res) return
  yield put({
    type: 'UPDATE_CAL_KPI_METRICS',
    payload: res.keyMets,
  })
}

function* getKpis(action: ReduxAction): IterableIterator<ReduxAction | any> {
  const { fromDate, toDate, metric } = action.payload
  const res: any = yield API.getKpis(fromDate, toDate, metric.toLowerCase())
  if (!res) return
  const data = res.metricData
  const metrics = data.map((keyMetric: any): any => [
    apiDateStringToDate(keyMetric.date).toString(),
    keyMetric,
  ])
  yield put({
    type: 'UPDATE_CAL_KPIS',
    payload: metrics,
  })
}

function* getGoalMetrics(): IterableIterator<ReduxAction | any> {
  const res: any = yield API.getGoalMetrics()
  if (!res) return

  const metrics = res.goals.map((metric: any): any => metric.displayName)
  yield put({
    type: 'UPDATE_CAL_GOAL_METRICS',
    payload: metrics,
  })
}

function* getCampaignCodesAsync(
  action?: ReduxAction,
): IterableIterator<ReduxAction | any> {
  let res
  if (action && action.payload) {
    const { startDate, endDate } = action?.payload
    res = yield API.getPreviousGeneratedCodes({ startDate, endDate })
  } else {
    res = yield API.getPreviousGeneratedCodes({})
  }

  if (!res) return
  yield put({
    type: 'GET_CAMPAIGN_CODES',
    payload: res,
  })
}

// function* getBatchCampaignCodesAsync(
//   action?: ReduxAction,
// ): IterableIterator<ReduxAction | any> {
//   let res
//   if (action && action.payload) {
//     const { startDate, endDate } = action?.payload
//     res = yield API.getBatchPreviousGeneratedCodes({ startDate, endDate })
//   } else {
//     res = yield API.getBatchPreviousGeneratedCodes({})
//   }

//   if (!res) return
//   yield put({
//     type: 'GET_BATCH_CAMPAIGN_CODES',
//     payload: res,
//   })
// }

function* getBatchQueryAsync(
  action?: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const { batch } = action?.payload
  const res = yield API.getBatchQueryAsync(batch)

  if (!res) return
  yield put({
    type: 'GET_BATCH_QUERY',
    payload: res,
  })
}

function* getBatchUsersCampaignCodesAsync(): IterableIterator<
  ReduxAction | any
> {
  const res = yield API.getBatchUsersPreviousGeneratedCodes()
  if (!res) return
  yield put({
    type: 'GET_BATCH_USERS_CAMPAIGN_CODES',
    payload: res,
  })
}

function* getAAFTPDetailsAsync(): IterableIterator<ReduxAction | any> {
  const res = yield API.getAAFTPDetails()
  if (!res) return
  yield put({
    type: 'GET_FTP_DETAILS',
    payload: res,
  })
}

function* getUsersCampaignCodesAsync(
  action?: ReduxAction,
): IterableIterator<ReduxAction | any> {
  let res
  if (action && action.payload) {
    const { startDate } = action?.payload
    res = yield API.getUsersPreviousGeneratedCodes({ startDate })
  } else {
    res = yield API.getUsersPreviousGeneratedCodes({})
  }

  if (!res) return
  yield put({
    type: 'GET_USERS_CAMPAIGN_CODES',
    payload: res,
  })
}

function* getCampaignCodesByAccountAsync(): IterableIterator<
  ReduxAction | any
> {
  yield put({
    type: 'START_LOADING_CAMPAIGN_CODES',
  })

  // const res = yield API.getCampaignCodesByAccount()
  const res: any = yield getCodesByAccount()

  if (!res) {
    yield put({
      type: 'FINISH_LOADING_CAMPAIGN_CODES',
    })
    return
  }
  yield put({
    type: 'GET_CAMPAIGN_CODES',
    payload: res.data?.codesByAccount.codeList,
  })
}

function* getCampaignCodeGeneratorAsync(): IterableIterator<ReduxAction | any> {
  const res = yield API.getCampaignCodeGeneratorStructure()
  if (!res) return
  yield put({
    type: 'GET_CAMPAIGN_CODE_GENERATOR',
    payload: res,
  })
}

function* updateCampaignCodeGeneratorAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.updateCampaignCodeGeneratorStructure(action.payload)
}

function* updateCampaignCodeGeneratorOrderAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.updateCampaignCodeGeneratorOrder(action.payload)

  // Always load the latest codes after updates
  const res = yield API.getCampaignCodeGeneratorStructure()
  if (!res) return
  yield put({
    type: 'GET_CAMPAIGN_CODE_GENERATOR',
    payload: res,
  })
}

function* updateGeneratorParameterAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.updateGeneratorParameter(action.payload)

  // Always load the latest codes after updates
  const res = yield API.getCampaignCodeGeneratorStructure()
  if (!res) return
  yield put({
    type: 'GET_CAMPAIGN_CODE_GENERATOR',
    payload: res,
  })
}

function* updateGeneratorParameterSelectDeleteAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const res = yield API.updateGeneratorParameterSelectDelete(action.payload)
  if (res !== false) {
    yield put({
      type: 'UPDATE_CAMPAIGN_CODE_GENERATOR_SELECT',
      payload: res,
    })
  }
}

function* updateGeneratorParameterSelectUpdateAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const res = yield API.updateGeneratorParameterSelectUpdate(action.payload)

  if (!res) return

  yield put({
    type: 'UPDATE_CAMPAIGN_CODE_GENERATOR_SELECT_UPDATE',
    payload: res,
  })
}

function* updateGeneratorParameterSelectAddOptionAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  const res = yield API.updateGeneratorParameterSelectAddOption(action.payload)
  if (!res) return
  yield put({
    type: 'UPDATE_CAMPAIGN_CODE_GENERATOR_SELECT_ADD',
    payload: res,
  })
}

function* deleteGeneratorParameterAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.deleteGeneratorParameter(action.payload)

  // Always load the latest codes after updates
  const res = yield API.getCampaignCodeGeneratorStructure()
  if (!res) return
  yield put({
    type: 'GET_CAMPAIGN_CODE_GENERATOR',
    payload: res,
  })
}

function* addGeneratorParameterAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield API.addGeneratorParameter(action.payload)

  // Always load the latest codes after updates
  const res = yield API.getCampaignCodeGeneratorStructure()
  if (!res) return
  yield put({
    type: 'GET_CAMPAIGN_CODE_GENERATOR',
    payload: res,
  })
}

function* updateCampaignCodeAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'UPDATE_CAMPAIGN_CODE',
    payload: action.payload.compressed,
  })
  // This is to make sure the latest code get fetched fresh
  yield put({
    type: 'EMPTY_USERS_CAMPAIGN_CODES',
  })

  try {
    const payload: any = updateCode(action.payload.full)
    if (payload) {
      // this is necessary for keys that are not in
      // the initial state of graphql reducer
      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['updateCode'] },
      })
      return payload
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['updateCode'] },
    })
  }
  return false
}

function* updateMarketingPermissionGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield updateMarketingPermission(action.payload)
    if (payload) {
      const { userAccountSettings } = payload.data
      const {
        acceptedMarketing,
      } = userAccountSettings.updateMarketingPermission
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { currentUser: { acceptedMarketing } },
      })
      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['updateMarketingPermission'] },
      })
      return payload
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['updateMarketingPermission'] },
    })
  }
  return false
}

function* updateQrSettingsGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield updateQrSettings(action.payload)
    if (payload) {
      const { updateQrSettings: result } = payload.data
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: { currentAccount: { qrSettings: result?.qrSettings } },
      })
      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['updateQrSettings'] },
      })
      return payload
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['updateQrSettings'] },
    })
  }
  return false
}

function* deleteCampaignCodesAsync(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'DELETE_CAMPAIGN_CODES',
    payload: action.payload,
  })

  const thread = [...action.payload]

  while (thread.length > 0) {
    try {
      const codeID = thread.pop()
      yield deleteCode({ codeID })
    } catch {
      yield put({
        type: 'ERROR_GRAPHQL_DATA',
        payload: { error: ['deleteCode'] },
      })
    }
  }

  // const res = yield API.deleteCampaignCodes(action.payload)
  return false
}

function* saveToCopy(action: ReduxAction): IterableIterator<ReduxAction | any> {
  yield put({
    type: 'SET_COPY',
    payload: action.payload,
  })
}

function* getOnboardingStatusGQL(): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield getOnboardingSectionsProgress()
    if (payload) {
      yield put({
        type: 'UPDATE_ONBOARDING_SECTIONS',
        payload: payload.data,
      })
    }
  } catch {
    // yield put({
    //   type: 'ERROR_GRAPHQL_DATA',
    //   payload: { error: ['currentAccount', 'currentUser'] },
    // })
  }
}

function* updatePreferredHomepageGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield updatePreferredHomepage(action.payload)
    if (payload) {
      yield put({
        type: 'UPDATE_PREFERRED_HOMEPAGE',
        payload: { currentUser: payload.data.updateUserHomepage },
      })
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['preferredHomepage'] },
    })
  }
}

function* saveLinkPerformanceReportGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield saveLinkPerformanceReport(action.payload)
    if (payload) {
      yield put({
        type: 'UPDATE_REPORT_GRAPHQL_DATA',
        payload: { report: payload.data.report.saveLinkPerformanceReport },
      })

      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['saveLinkPerformanceReport'] },
      })
      return payload
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['saveLinkPerformanceReport'] },
    })
  }
  return false
}

function* removeSavedLinkPerformanceReportGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield removeSavedLinkPerformanceReport(action.payload)

    if (payload) {
      yield put({
        type: 'UPDATE_GRAPHQL_DATA',
        payload: {
          listSavedLinkPerformanceReports:
            payload.data.report.removeSavedLinkPerformanceReport,
        },
      })

      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['removeSavedLinkPerformanceReport'] },
      })
      return payload
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['removeSavedLinkPerformanceReport'] },
    })
  }
  return false
}

function* updateSavedLinkPerformanceReportGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const payload: any = yield updateSavedLinkPerformanceReport(action.payload)
    if (payload) {
      yield put({
        type: 'UPDATE_REPORT_GRAPHQL_DATA',
        payload: {
          report: payload.data.report.updateSavedLinkPerformanceReport,
        },
      })

      yield put({
        type: 'CLEAR_ERROR_GRAPHQL_DATA',
        payload: { error: ['updateSavedLinkPerformanceReport'] },
      })
      return payload
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['updateSavedLinkPerformanceReport'] },
    })
  }
  return false
}

function* updateLinkPerformanceUserPrefsGQL(
  action: ReduxAction,
): IterableIterator<ReduxAction | any> {
  try {
    const { newOrder, saveUpdate } = action.payload
    if (!saveUpdate) {
      yield put({
        type: 'UPDATE_GRAPHQL_SAVED_REPORTS_DATA',
        payload: {
          userPreferredOrder: newOrder,
        },
      })
    } else {
      const payload: any = yield updateLinkPerformanceUserPrefs({ newOrder })
      if (payload) {
        yield put({
          type: 'UPDATE_GRAPHQL_DATA',
          payload: {
            listSavedLinkPerformanceReports:
              payload.data.report.updateLinkPerformanceUserPrefs,
          },
        })

        yield put({
          type: 'CLEAR_ERROR_GRAPHQL_DATA',
          payload: { error: ['updateLinkPerformanceUserPrefs'] },
        })
        return payload
      }
    }
  } catch {
    yield put({
      type: 'ERROR_GRAPHQL_DATA',
      payload: { error: ['updateLinkPerformanceUserPrefs'] },
    })
  }
  return false
}

export default function* rootSaga(): IterableIterator<any> {
  yield takeEvery('CHECK_AUTHENTICATION_ASYNC', checkAuthenticationAsync)
  yield takeEvery('AUTHENTICATE_ASYNC', authenticateAsync)
  yield takeEvery('GET_POLICY_ASYNC', getPolicyAsync)
  yield takeEvery('CREATE_NEW_BILLING_USER_ASYNC', createNewBillingUserAsync)
  yield takeEvery('CREATE_NEW_CLIENT_ASYNC', createNewClientAsync)
  yield takeEvery('GET_GOOGLE_PERMISSION_ASYNC', getGooglePermissions)
  yield takeEvery('HANDLE_GOOGLE_RESPONSE_ASYNC', handleGoogleResponseAsync)
  yield takeEvery('HANDLE_ADOBE_RESPONSE_ASYNC', handleAdobeResponseAsync)
  yield takeEvery('LOGIN_MICROSOFT_SSO_ASYNC', loginMicrosoftSSOAsync)
  yield takeEvery('LOGIN_GOOGLE_SSO_ASYNC', loginGoogleSSOAsync)

  yield takeEvery('REQUEST_PASSWORD_RESET_ASYNC', requestPasswordResetAsync)
  yield takeEvery('RESET_PASSWORD_ASYNC', resetPasswordAsync)
  yield takeEvery('GET_CALENDAR_ANOMALY_RANGE_ASYNC', getCalendarAnomalies)
  yield takeEvery('GET_CALENDAR_EVENTS_ASYNC', getCalendarEvents)
  yield takeEvery('GET_CALENDAR_CAMPAIGNS_ASYNC', getCalendarCampaigns)
  yield takeEvery('GET_CALENDAR_GA_CAMPAIGNS_ASYNC', getGaCampaigns)
  yield takeEvery('GET_CALENDAR_KPI_METRICS_ASYNC', getKpiMetrics)
  yield takeEvery('GET_CALENDAR_KPIS_ASYNC', getKpis)
  yield takeEvery('GET_CALENDAR_GOAL_METRICS_ASYNC', getGoalMetrics)
  yield takeEvery('GET_ANOMALY_RANGE_ASYNC', getAnomalyDataRange)
  // yield takeEvery('GET_ANOMALY_BREAKDOWN_ASYNC', getAnomalyBreakdown)
  yield takeEvery('GET_ANOMALY_BREAKDOWN_CAUSES_ASYNC', getCausesAndSolutions)
  yield takeEvery('GET_CAMPAIGN_CODES_ASYNC', getCampaignCodesAsync)
  // yield takeEvery('GET_BATCH_CAMPAIGN_CODES_ASYNC', getBatchCampaignCodesAsync)
  yield takeEvery('GET_USERS_CAMPAIGN_CODES_ASYNC', getUsersCampaignCodesAsync)
  yield takeEvery('GET_AA_FTP_DETAILS_ASYNC', getAAFTPDetailsAsync)
  yield takeEvery('UPDATE_CAMPAIGN_CODE_ASYNC', updateCampaignCodeAsync)
  yield takeEvery('DELETE_CAMPAIGN_CODES_ASYNC', deleteCampaignCodesAsync)
  yield takeEvery(
    'GET_BATCH_USERS_CAMPAIGN_CODES_ASYNC',
    getBatchUsersCampaignCodesAsync,
  )
  yield takeEvery('GET_BATCH_QUERY_ASYNC', getBatchQueryAsync)
  yield takeEvery(
    'GET_CAMPAIGN_CODE_GENERATOR_ASYNC',
    getCampaignCodeGeneratorAsync,
  )
  yield takeEvery(
    'UPDATE_CAMPAIGN_CODE_GENERATOR_ASYNC',
    updateCampaignCodeGeneratorAsync,
  )
  yield takeEvery(
    'UPDATE_CAMPAIGN_CODE_GENERATOR_ORDER_ASYNC',
    updateCampaignCodeGeneratorOrderAsync,
  )
  yield takeEvery(
    'UPDATE_GENERATOR_PARAMETER_ASYNC',
    updateGeneratorParameterAsync,
  )
  yield takeEvery(
    'UPDATE_GENERATOR_PARAMETER_SELECT_UPDATE_ASYNC',
    updateGeneratorParameterSelectUpdateAsync,
  )
  yield takeEvery(
    'UPDATE_GENERATOR_PARAMETER_SELECT_ADD_OPTION_ASYNC',
    updateGeneratorParameterSelectAddOptionAsync,
  )
  yield takeEvery(
    'UPDATE_GENERATOR_PARAMETER_SELECT_DELETE_ASYNC',
    updateGeneratorParameterSelectDeleteAsync,
  )
  yield takeEvery(
    'DELETE_GENERATOR_PARAMETER_ASYNC',
    deleteGeneratorParameterAsync,
  )
  yield takeEvery('ADD_GENERATOR_PARAMETER_ASYNC', addGeneratorParameterAsync)

  yield takeEvery(
    'GET_CAMPAIGN_CODES_BY_ACCOUNT_ASYNC',
    getCampaignCodesByAccountAsync,
  )
  yield takeEvery('GET_USER_DETAILS_ASYNC', getUserDetails)
  yield takeEvery('SWITCH_ACCOUNT_ASYNC', switchAccount)
  yield takeEvery('LOG_ACTIONS_ASYNC', logActionAsync)
  yield takeEvery('REGISTER_ASYNC', registerAsync)
  yield takeEvery('GET_ALL_USERS_ASYNC', getAllUsers)
  yield takeEvery('GET_SELECTED_ACCOUNT_ASYNC', getSelectedAccount)
  yield takeEvery('SUBSCRIBE_TO_AUDIT', subscribeToAudit)
  yield takeEvery('REQUEST_AN_EXPERT_ASYNC', requestAnExpert)
  yield takeEvery('ADD_ACCOUNT_ASYNC', addAccount)
  yield takeEvery('ADD_USER_ASYNC', addNewUsersToAccountAsync)
  yield takeEvery('REMOVE_USER_ASYNC', removeUserAsync)
  yield takeEvery('UPDATE_USER_PERMISSIONS_ASYNC', updateUserPermissionsAsync)
  yield takeEvery('REVOKE_EMAIL_INVITE_ASYNC', revokeEmailInviteAsync)
  yield takeEvery('CHANGE_USER_PERMISSIONS_ASYNC', changeUserPermissions)
  yield takeEvery('CLOSE_ACCOUNTS_ASYNC', closeAccounts)
  yield takeEvery('DELETE_TOKEN', logout)
  yield takeEvery('GET_EXPLAIN_SETUP_ASYNC', getExplainSetupAsync)
  yield takeEvery('GET_GA_METRICS_ASYNC', getAllGaMetricsAsync)
  yield takeEvery('SAVE_EXPLAIN_SETUP_ASYNC', saveExplainSetupAsync)
  yield takeEvery('ADD_METRIC_ASYNC', addMetricAsync)
  yield takeEvery('UPDATE_METRIC_ASYNC', updateMetricAsync)
  yield takeEvery('REMOVE_METRIC_ASYNC', removeMetricAsync)
  yield takeEvery('GET_CURRENT_KEY_METRICS_ASYNC', getCurrentKeyMetricsAsync)
  yield takeEvery(
    'GET_ANOMALY_DESCRIPTION_ASYNC',
    getAnomalyDateDescriptionAsync,
  )
  yield takeEvery(
    'UPDATE_ANOMALY_DESCRIPTION_ASYNC',
    updateAnomalyDescriptionAsync,
  )
  yield takeEvery('GET_ALL_GA_DIMENSIONS_ASYNC', getAllGaDimensionsAsync)
  yield takeEvery('LOAD_CREDENTIALS_ASYNC', loadCredentialsAsync)
  yield takeEvery(
    'GET_ENTERPRISE_BILLING_STATE_ASYNC',
    getEnterpriseBillingStateAsync,
  )
  yield takeEvery(
    'UPDATE_ENTERPRISE_BILLING_STATE_ASYNC',
    updateEnterpriseBillingStateAsync,
  )
  yield takeEvery('ADD_ENTERPRISE_ACCOUNT_ASYNC', addEnterpriseAccountAsync)
  yield takeEvery('GET_USER_LIST_ASYNC', getUserListAsync)
  yield takeEvery(
    'GET_REMOTE_DASHBOARD_LIST_ASYNC',
    getRemoteDashboardListAsync,
  )
  yield takeEvery(
    'UPDATE_ANOMALY_REDUCERS_ONLY',
    updateAnomalyDescriptionReducersOnly,
  )
  yield takeEvery('GET_STORED_CAMPAIGN_STATS_ASYNC', getStoredCodeStatsAsync)
  yield takeEvery('SAVE_TO_COPY', saveToCopy)
  yield takeEvery('GET_VALIDATE_RESULTS_ASYNC', getValidateResultsAsync)
  yield takeEvery('VALIDATE_URL_LIST_ASYNC', validateUrlListAsync)
  yield takeEvery(
    'UPDATE_GENERATOR_MASTER_SETTINGS_ASYNC',
    updateGeneratorMasterSettingsAsync,
  )
  yield takeEvery(
    'UPDATE_ACCOUNT_DESCRIPTION_ASYNC',
    updateAccountDescriptionAsync,
  )
  yield takeEvery('RESET_LOGIN_ATTEMPT_FAIL_ASYNC', resetLoginAttemptFailAsync)
  yield takeEvery(
    'RESET_ONBOARDING_CREATE_USER_FAILED_ASYNC',
    resetOnboardingCreateUserFailedAsync,
  )
  yield takeEvery('GET_UPDATE_REQUEST_ASYNC', getUpdateRequestsAsync)
  yield takeEvery('SET_UPDATE_REQUEST_ASYNC', setUpdateRequestsAsync)
  yield takeEvery('SEND_UPDATE_REQUEST_ASYNC', sendUpdateRequestsAsync)
  yield takeEvery('GET_USER_ACCOUNT_DETAILS_GQL', getUserAccountDetailsGQL)
  yield takeEvery('ADD_USER_TO_ACCOUNT_GQL', addUserToAccountGQL)
  yield takeEvery('GET_METRIC_DATA_GQL', getCampaignLinkDashboardMetricDataGQL)
  yield takeEvery(
    'GET_METRIC_CLICK_DATA_GQL',
    getCampaignLinkDashboardMetricClickDataGQL,
  )
  yield takeEvery('GET_DASHBOARD_GRAPH_GQL', getCampaignLinkDashboardGraphGQL)
  yield takeEvery(
    'GET_DASHBOARD_GRAPH_CLICK_DATA_GQL',
    getCampaignLinkDashboardGraphClickDataGQL,
  )
  yield takeEvery('GET_DASHBOARD_TABLE_GQL', getCampaignLinkDashboardTableGQL)
  yield takeEvery('GET_DASHBOARD_META_GQL', getCampaignLinkDashboardMetaGQL)
  yield takeEvery(
    'GET_DASHBOARD_META_DEFAULTS_GQL',
    getCampaignLinkDashboardMetaDefaultsGQL,
  )
  yield takeEvery('SET_DASHBOARD_SETTINGS_GQL', setDashboardSettingsGQL)
  yield takeEvery('GET_STORED_CODE_STATS_GQL', getStoredCodeStatsGQL)
  yield takeEvery('GET_SHORT_LINK_CANDIDATES_GQL', getShortLinkCandidatesGQL)
  yield takeEvery(
    'VALIDATE_SHORT_LINK_CANDIDATES_GQL',
    validateShortLinkCandidateGQL,
  )
  yield takeEvery('ADD_CODES_GQL', addCodesGQL)
  yield takeEvery('GET_CODES_BY_USER_GQL', getCodesByUserGQL)
  yield takeEvery(
    'REMOVE_SHORT_LINK_CANDIDATES_GQL',
    removeShortLinkCandidatesGQL,
  )
  yield takeEvery('SET_CURRENT_DROPDOWN', setCurrentDropDown)
  yield takeEvery('SET_CURRENT_TOOLTIP', setCurrentTooltip)
  yield takeEvery('GET_ONBOARDING_STATUS', getOnboardingStatusGQL)
  yield takeEvery('GET_DATA_SOURCE_METRIC_LIST_GQL', getDataSourceMetricListGQL)
  yield takeEvery('UPDATE_PREFERRED_HOMEPAGE_GQL', updatePreferredHomepageGQL)
  yield takeEvery('GET_SETTINGS_GQL', getSettingsGQL)
  yield takeEvery('UPDATE_QR_SETTINGS_GQL', updateQrSettingsGQL)
  yield takeEvery('SAVE_LINK_REPORT_GQL', saveLinkPerformanceReportGQL)
  yield takeEvery(
    'UPDATE_LINK_REPORT_LIST_GQL',
    updateLinkPerformanceUserPrefsGQL,
  )
  yield takeEvery('REMOVE_LINK_REPORT_GQL', removeSavedLinkPerformanceReportGQL)
  yield takeEvery('UPDATE_LINK_REPORT_GQL', updateSavedLinkPerformanceReportGQL)

  yield takeEvery(
    'LIST_SAVED_REPORTS_GQL',
    listSavedLinkPerformanceReportsAsync,
  )
  yield takeEvery(
    'UPDATE_MARKETING_PERMISSION_GQL',
    updateMarketingPermissionGQL,
  )
}
